// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/audit/GovernanceAuditPanel.jsx

import React, { useMemo, useState } from "react";
import GovernanceEventDetailDrawer from "./GovernanceEventDetailDrawer";
import GovernanceEventFilters from "./GovernanceEventFilters";
import GovernanceEventSummaryCards from "./GovernanceEventSummaryCards";
import GovernanceEventTimeline from "./GovernanceEventTimeline";
import GovernanceExportModal from "./GovernanceExportModal";

function matchesSearch(event, query) {
  if (!query) return true;

  const normalizedQuery = query.toLowerCase();

  return [
    event.event_title,
    event.event_type,
    event.event_category,
    event.event_summary,
    event.actor_name,
    event.actor_id,
    event.target_name,
    event.target_id,
    event.source_table,
  ]
    .filter(Boolean)
    .some((value) =>
      String(value).toLowerCase().includes(normalizedQuery)
    );
}

export default function GovernanceAuditPanel({
  events = [],
  summary,
  categories = [],
  latestExport,
  actions = {},
  saving,
}) {
  const [filters, setFilters] = useState({
    q: "",
    category: "",
    severity: "",
  });
  const [selectedEventId, setSelectedEventId] = useState(
    events[0]?.id || null
  );
  const [exportResult, setExportResult] = useState(null);

  const normalizedCategories = categories.length
    ? categories
    : [
        { key: "organization", label: "Organization" },
        { key: "users", label: "Users" },
        { key: "roles", label: "Roles & Permissions" },
        { key: "connectors", label: "Connectors" },
        { key: "audit", label: "Audit" },
      ];

  const visibleEvents = useMemo(
    () =>
      events.filter((event) => {
        if (
          filters.category &&
          event.event_category !== filters.category
        ) {
          return false;
        }

        if (
          filters.severity &&
          event.severity !== filters.severity
        ) {
          return false;
        }

        return matchesSearch(event, filters.q);
      }),
    [events, filters]
  );

  const selectedEvent = useMemo(
    () =>
      visibleEvents.find(
        (event) => event.id === selectedEventId
      ) ||
      visibleEvents[0] ||
      null,
    [visibleEvents, selectedEventId]
  );

  const handleExport = async () => {
    const result = await actions.exportGovernanceEvents?.({
      export_format: "json",
      category: filters.category || null,
      severity: filters.severity || null,
      q: filters.q || null,
      limit: 250,
    });

    setExportResult(result || latestExport || null);
  };

  return (
    <section className="enterprise-admin-section-panel enterprise-admin-governance-audit-panel">
      <div className="enterprise-admin-section-header">
        <p className="enterprise-admin-eyebrow">
          Governance Audit
        </p>

        <h2>Compliance traceability ledger</h2>

        <p>
          Review explainable, tenant-scoped administrative
          actions across organization, users, roles,
          permissions, and connectors.
        </p>
      </div>

      <GovernanceEventSummaryCards summary={summary} />

      <GovernanceEventFilters
        categories={normalizedCategories}
        filters={filters}
        onChange={setFilters}
        onExport={handleExport}
        saving={saving}
      />

      <div className="enterprise-admin-two-column enterprise-admin-governance-layout">
        <GovernanceEventTimeline
          events={visibleEvents}
          selectedEvent={selectedEvent}
          onSelect={(event) =>
            setSelectedEventId(event.id)
          }
        />

        <GovernanceEventDetailDrawer
          event={selectedEvent}
        />
      </div>

      <GovernanceExportModal
        exportResult={exportResult || latestExport}
        onClose={() => setExportResult(null)}
      />
    </section>
  );
}